<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-1">
      <label class="font-semibold" :for="name">
        {{ label }}<span v-if="required" class="text-red-600">*</span>
      </label>

      <Multiselect
        ref="dropdown"
        v-model="parentInputValue"
        @clear="onClearParent"
        :options="parentOptions"
        :multiple="false"
        :canClear="true"
      />

      <p class="text-red-500" v-show="parentErrorMessage || parentMeta.valid">
        {{ parentErrorMessage }}
      </p>
    </div>

    <template v-if="parentInputValue">
      <div v-for="el in children" :key="el.parent" class="flex flex-col gap-1">
        <label class="font-semibold" :for="el.slug">{{ el.label }}</label>

        <Multiselect
          v-model="childrenValues[el.slug]"
          :options="el.children"
          :multiple="false"
          :canClear="true"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useField } from 'vee-validate';
import * as yup from 'yup';
import Multiselect from '@vueform/multiselect';

import { getNestedSelectValues } from '@/helpers/nestedSelectCache'; // ✅ КЭШ

const { t } = useI18n();

const props = defineProps({
  value: { type: String, default: undefined },
  label: { type: String, required: true },
  name: { type: String, required: true },
  required: { type: Boolean, default: true },
  options: { type: Array },
  property: { type: Number, default: undefined },
  defaultMultiValues: { type: Array, default: undefined },
});

let rules = yup.number().required(t('warning.REQUIRED_MESSAGE'));

const {
  meta: parentMeta,
  value: parentInputValue,
  errorMessage: parentErrorMessage,
  validate: parentValidate,
} = useField(props.name, rules, {
  initialValue: props.value,
});

const dropdown = ref(null);

const isLoading = ref(false);

const parentOptions = ref([]); // то, что показываем в multiselect
const parentOptionsCopy = ref([]); // сырой объект с children и children_property

const children = ref([]); // массив уровней (в твоём случае 1 уровень: модели выбранной марки)
const childrenValues = ref({}); // выбранная модель по slug бренда

function buildChildrenForParentId(parentId) {
  children.value = [];
  childrenValues.value = {};

  if (!parentId) return;

  const detail = parentOptionsCopy.value.find(el => el.id === parentId);
  if (!detail || !detail.children) return;

  const children_ = detail.children.map(item => ({
    value: item.id,
    label: item.name,
    slug: item.slug,
  }));

  // для одной марки — один список моделей
  childrenValues.value[detail.slug] = null;

  children.value = [
    {
      parent: parentId,
      label: `${detail.children_property.name} | ${detail.name}`,
      slug: detail.slug,
      children: children_,
    },
  ];
}

function onParentChange(val) {
  buildChildrenForParentId(val);
}

function onClearParent() {
  children.value = [];
  childrenValues.value = {};
}

watch(parentInputValue, newVal => {
  buildChildrenForParentId(newVal);
});

const validateField = async () => {
  const parentValidatedData = await parentValidate();

  if (!parentInputValue.value) {
    return { value: undefined, valid: false, mode: 'multi' };
  }

  const childValues = Object.values(childrenValues.value).filter(Boolean);

  return {
    value: [parentInputValue.value, ...childValues],
    valid: parentValidatedData.valid,
    mode: 'multi',
    property: props.property,
  };
};

defineExpose({ validateField });

async function fetchAllOptionsFromCache() {
  isLoading.value = true;
  try {
    // ✅ ВОТ ГЛАВНОЕ: берём из localStorage или обновляем раз в 7 дней
    const data = await getNestedSelectValues(props.property);

    // data = [{id,name,slug,children,children_property}, ...]
    parentOptions.value = data.map(item => ({
      value: item.id,
      label: item.name,
      slug: item.slug,
      children: item.children || [],
      children_property: item.children_property || {},
    }));

    // для поиска детей по id родителя нужен сырой объект
    parentOptionsCopy.value = data;
  } catch (error) {
    console.error('Error fetching cached options:', error);
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  fetchAllOptionsFromCache();
});
</script>

<style>
@import '@vueform/multiselect/themes/tailwind.css';
</style>
